import { Fragment, type ComponentType, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, type To } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /** Router target. A crumb with neither `to` nor `onClick` is the current location. */
  to?: To;
  onClick?: () => void;
}

interface BreadcrumbBarProps {
  /** First crumb carries the back arrow; the last is usually the current page. */
  crumbs: Crumb[];
  /** Rich current-location node (e.g. document title + status badge), rendered
   *  after the crumbs behind its own divider. */
  current?: ReactNode;
  /** Right-aligned action slot; wraps to its own full-width line on narrow screens. */
  actions?: ReactNode;
  className?: string;
}

const LINK_CLASS =
  'inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium text-slate-500 dark:text-slate-400 ' +
  'hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-300 transition-colors';

/**
 * The "← Docs | {source} | …" bar every full editor and docs sub-page shares
 * (specs/changes/consolidated-full-editor R1/R7). Callers build the crumb
 * targets themselves — including the query string, which on localhost carries
 * the ?tenant host selection and must survive back-navigation.
 */
export function BreadcrumbBar({ crumbs, current, actions, className }: BreadcrumbBarProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 px-3 sm:px-4 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 shrink-0',
        className,
      )}
    >
      {crumbs.map((crumb, i) => {
        const Icon = crumb.icon;
        const inner = (
          <>
            {i === 0 && <ArrowLeft className="w-3.5 h-3.5 shrink-0" />}
            {Icon && <Icon className="w-4 h-4 text-slate-400 shrink-0" />}
            <span className="truncate">{crumb.label}</span>
          </>
        );
        return (
          <Fragment key={`${crumb.label}-${i}`}>
            {i > 0 && <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 shrink-0" />}
            {crumb.to ? (
              <Link to={crumb.to} className={cn(LINK_CLASS, i === 0 && '-ml-1')} title={`Back to ${crumb.label}`}>
                {inner}
              </Link>
            ) : crumb.onClick ? (
              <button onClick={crumb.onClick} className={cn(LINK_CLASS, i === 0 && '-ml-1')} title={`Back to ${crumb.label}`}>
                {inner}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 min-w-0 text-sm font-medium text-slate-700 dark:text-slate-300">
                {inner}
              </span>
            )}
          </Fragment>
        );
      })}
      {current && (
        <>
          <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 shrink-0" />
          {current}
        </>
      )}
      {actions && (
        <div className="flex flex-wrap items-center gap-2 basis-full sm:basis-auto sm:ml-auto">{actions}</div>
      )}
    </div>
  );
}
